import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { useState, useEffect, useCallback } from 'react'
import { ShieldAlert, AlertTriangle, X } from 'lucide-react'
import { useAuth } from '@/shared/hooks/useAuth'
import {
  getSupplierBypass,
  activateSupplierBypass,
  MIN_BYPASS_REASON,
  type SupplierBypassState,
} from '@/features/proveedores/api/supplierBypass.service'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

// Bypass de expediente (C-NEW-03 / SP-212), conectado al back de SP-213:
// GET  /internal/suppliers/:id/bypass  → estado + historial (últimos 10)
// POST /internal/suppliers/:id/bypass  → { reason } (mínimo 20 caracteres)
// El permiso de sesión es canBypassSupplierDocumentValidation.

interface BypassExpedienteSectionProps {
  supplierId: string
  // Por defecto se gatea con el permiso real de la sesión; el prop queda para
  // forzarlo desde afuera si alguna vista lo necesita.
  canActivate?: boolean
  // Ver el historial (real: rol Compras / Contraloría).
  canViewHistory?: boolean
  // Un proveedor ya aprobado tiene el expediente completo: no hay nada que
  // saltarse, así que no se ofrece activar. El historial sí se conserva por si
  // hubo excepciones antes de aprobarlo.
  supplierApproved?: boolean
  // El bypass se lee aca adentro, pero quien decide si el expediente se puede
  // aprobar es el componente de arriba: se le avisa el estado para que no tenga
  // que pedir lo mismo por su cuenta.
  onEstadoBypass?: (activo: boolean) => void
}

// El back manda `message` como arreglo en los errores de validacion (422 de
// NestJS), asi que se normaliza antes de pintarlo.
const errorMessage = (err: any, fallback: string): string => {
  const m = err?.message
  if (Array.isArray(m)) return m.filter(Boolean).join(' ')
  return typeof m === 'string' && m.trim() ? m : fallback
}

const BypassExpedienteSection = ({
  supplierId,
  canActivate,
  canViewHistory = true,
  supplierApproved = false,
  onEstadoBypass,
}: BypassExpedienteSectionProps) => {
  const { user } = useAuth()
  // El back valida la columna can_bypass_supplier_document_validation del
  // usuario y no exceptúa al master, así que acá se usa el mismo criterio
  // exacto: si mostráramos el botón por ser master, el POST daría 403.
  const puedeActivar = canActivate ?? user?.canBypassSupplierDocumentValidation === true

  const [state, setState] = useState<SupplierBypassState | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!supplierId) return
    setLoading(true)
    setLoadError(null)
    try {
      setState(await getSupplierBypass(supplierId))
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setLoadError(errorMessage(err, 'No se pudo cargar el estado del bypass.'))
    } finally {
      setLoading(false)
    }
  }, [supplierId])

  useEffect(() => {
    load()
  }, [load])

  // Se avisa hacia arriba en cuanto se conoce el estado y cada vez que cambia
  // (al activarlo se recarga el GET), no solo al montar.
  const bypassActivo = state?.bypassActive === true
  useEffect(() => {
    if (!state) return
    onEstadoBypass?.(bypassActivo)
  }, [state, bypassActivo, onEstadoBypass])

  const reasonValido = reason.trim().length >= MIN_BYPASS_REASON

  const handleConfirm = async () => {
    if (!reasonValido) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      await activateSupplierBypass(supplierId, reason.trim())
      // El POST devuelve solo bypassActive/bypassReason: se relee el GET para
      // traer el historial con el registro nuevo ya formado por el back.
      await load()
      setModalOpen(false)
      setReason('')
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setSubmitError(errorMessage(err, 'No se pudo activar el bypass.'))
    } finally {
      setSubmitting(false)
    }
  }

  const closeModal = () => {
    if (submitting) return
    setModalOpen(false)
    setSubmitError(null)
  }

  const fmtDate = (iso: string) => {
    const d = new Date(iso)
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-MX')
  }

  const userName = (u: SupplierBypassState['history'][number]['user']) =>
    `${u?.firstName ?? ''} ${u?.lastName ?? ''}`.trim() || u?.email || '—'

  const active = bypassActivo ? { motivo: state?.bypassReason ?? '' } : null
  const historial = state?.history ?? []

  // Aprobado y sin excepciones registradas: no hay nada que mostrar, ni tarjeta
  // vacía de historial.
  if (supplierApproved && !loading && !loadError && !active && historial.length === 0) {
    return null
  }

  return (
    <div className="space-y-4 mb-6">
      {/* Banner ámbar — visible cuando hay bypass activo */}
      {active && (
        <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            <span className="font-semibold">
              Este proveedor opera con bypass de expediente activo
            </span>
            {active.motivo ? <> — {active.motivo}</> : null}
          </p>
        </div>
      )}

      {/* Fallo al leer el estado: se avisa en vez de aparentar que no hay bypass */}
      {loadError && (
        <div className="flex items-start gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-800">
            <p>{loadError}</p>
            <button
              onClick={load}
              className="mt-1 font-medium underline hover:no-underline"
            >
              Reintentar
            </button>
          </div>
        </div>
      )}

      {/* Sección de activación — solo con permiso y si no hay uno activo */}
      {puedeActivar && !supplierApproved && !active && !loading && !loadError && (
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-5">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Bypass de expediente
              </h3>
              <p className="text-sm text-gray-500 mt-0.5">
                Permite que el proveedor opere aunque su expediente no esté
                completo. La acción queda registrada.
              </p>
            </div>
          </div>
          <div className="mt-4">
            <button
              onClick={() => setModalOpen(true)}
              className={estiloDeBoton('primario')}
            >
              <ShieldAlert className="w-4 h-4" />
              Permitir operación sin expediente completo
            </button>
          </div>
        </div>
      )}

      {/* Historial de excepciones — Compras / Contraloría */}
      {canViewHistory && (
        <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
          <div className="px-5 py-4 border-b border-[#E5E7EB]">
            <h3 className="text-base font-semibold text-gray-900">
              Historial de excepciones
            </h3>
          </div>
          {loading ? (
            <p className="px-5 py-6 text-sm text-gray-500 text-center">Cargando…</p>
          ) : historial.length === 0 ? (
            <p className="px-5 py-6 text-sm text-gray-500 text-center">
              Sin excepciones registradas.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-[#E5E7EB]">
                <thead className="bg-[#FAF9FF]">
                  <tr>
                    <th className="px-5 py-3 text-left text-sm font-medium text-[#1B2128] whitespace-nowrap">Fecha</th>
                    <th className="px-5 py-3 text-left text-sm font-medium text-[#1B2128] whitespace-nowrap">Usuario</th>
                    <th className="px-5 py-3 text-left text-sm font-medium text-[#1B2128]">Motivo</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-[#E1E1E1]">
                  {historial.map((r) => (
                    <tr key={r.id} className="hover:bg-[#F4F7FB] transition-colors">
                      <td className="px-5 py-3 text-sm text-gray-700 whitespace-nowrap">{fmtDate(r.createdAt)}</td>
                      <td className="px-5 py-3 text-sm text-gray-700 whitespace-nowrap">{userName(r.user)}</td>
                      <td className="px-5 py-3 text-sm text-gray-700">{r.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal de activación */}
      {modalOpen && (
        <CapaDeModal zIndex={60}>
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <button
              onClick={closeModal}
              disabled={submitting}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-bold text-gray-900 pr-6">
              Permitir operación sin expediente completo
            </h2>
            <p className="text-sm text-gray-600 mt-2">
              Esta acción quedará registrada con tu usuario, la fecha y el
              motivo.
            </p>
            <label className="block text-sm font-medium text-gray-700 mt-4 mb-1">
              Motivo de la excepción <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2.5 rounded-lg text-sm text-gray-800 bg-[#F6F9FB] border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder={`Describe el motivo (mínimo ${MIN_BYPASS_REASON} caracteres)`}
            />
            <p className="text-xs text-gray-400 mt-1">
              {reason.trim().length}/{MIN_BYPASS_REASON} mínimo
            </p>

            {submitError && (
              <div className="flex items-start gap-2 mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{submitError}</p>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={closeModal}
                disabled={submitting}
                className={estiloDeBoton('secundario')}
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirm}
                disabled={!reasonValido || submitting}
                className={estiloDeBoton('primario')}
              >
                Confirmar
              </button>
            </div>
          </div>
        </CapaDeModal>
      )}
    </div>
  )
}

export default BypassExpedienteSection
